import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { Card, CardBody, CardTitle, UncontrolledTooltip, Badge } from 'reactstrap';
import { OrgId, PaymentId, PaymentMethodType } from '@business/entities/organization';
import { HTMLService, IntegrationService, CommonService } from '@business/services';
import { Button } from '@core/components/Button';
import { useObservable } from '@core/utils/hooks/rxjs';
import { showYesNo, WithTooltip } from '@modules/common';
import { MDBDataTable } from 'mdbreact';
import { IntegrationGroup, IntegrationType } from '@business/entities/integration';
import { EmptySection } from '@core/components/EmptySection';
import { Session } from '@modules/auth/session';
import { UserRole } from '@business/entities/account';
import { useHistory } from '@core/router';
import { FlashMessage } from '@core/components/FlashMessage';
import { openStripeFormModal } from '@modules/paymentForms/Stripe/StripeFormModal';
import { openSelectPaymentIntegrationModel } from './SelectPaymentIntegrationModel';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { R } from '@core/utils/r';
import { tap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { openAddEditPayment } from '../../AddEditPaymentModal';
import { OrganizationPageBloc } from '../../OrganizationPageBloc';

export interface PaymentMethodsProps {
  bloc: OrganizationPageBloc;
  orgId: OrgId;
}

export const PaymentMethods = memo((props: PaymentMethodsProps) => {
  const { bloc, orgId } = props;
  const organization = useObservable(bloc.organization$);
  const orgSubscriptions = useMemo(() => organization && organization.subscriptions, [organization]);
  const user = useObservable(Session.user$);
  const settings = useSettings();
  const methods = useMemo(() => settings?.integrations.filter(i => i.group === IntegrationGroup.payment), [settings]);
  const firstMethod = useMemo(() => R.first(methods), [methods]);
  const importType = useMemo(() => {
    if (!organization) return '';
    return organization.importType;
  }, [organization]);

  const paymentMethods = useMemo(() => {
    if (!organization) return [];
    return organization.methods;
  }, [organization]);

  const data = {
    columns: [
      {
        label: 'Title',
        field: 'name',
        width: 150,
      },
      {
        label: 'Type',
        field: 'cartType',
      },
      {
        label: 'Last 4 Digits',
        field: 'cardDigits',
      },
      {
        label: 'Exp Date',
        field: 'cardExpiry',
      },
      {
        label: 'Processor',
        field: 'service',
      },
      {
        label: 'Next Steps',
        field: 'nextSteps',
      },
    ],
    rows: paymentMethods,
  };

  const onRemove = useCallback(
    (paymentId: PaymentId, subs?: string[]) => {
      bloc.deletePaymentMethod(paymentId, orgId, subs).subscribe();
    },
    [bloc, orgId],
  );

  const onCopyPaymentLink = useCallback(() => {
    if (organization) {
      CommonService.copyToClipboard(organization.paymentMethodUrl);
      FlashMessage.success('Link Copied', 'Payment link has been copied to you clipboard');
    }
  }, [organization]);

  const makeDefault = useCallback(
    (paymentId: PaymentId) => {
      const defaultMethod = paymentMethods.find(p => p.default);
      const defaultMethodSubscriptions = orgSubscriptions && orgSubscriptions.filter(s => s.paymentMethodId === defaultMethod?.id);

      if (defaultMethodSubscriptions && defaultMethodSubscriptions.length === 0) {
        return bloc.makeDefault(paymentId).subscribe();
      }

      return of(null)
        .pipe(
          switchMap(() => {
            if (defaultMethodSubscriptions && defaultMethodSubscriptions.length > 0) {
              return showYesNo(
                'Update subscriptions payment method?',
                `There are active subscriptions that have the ${defaultMethod?.cardType} ending in ${defaultMethod?.cardDigits}, would you like to update all subscriptions to use the new default payment method?`,
                { btnYesLabel: 'YES', btnNoLabel: 'NO' },
              );
            }
            return of(false);
          }),
          tap(answer => {
            if (answer) {
              return bloc.updateSubPaymentMethod(paymentId).subscribe();
            } else {
              return bloc.makeDefault(paymentId).subscribe();
            }
          }),
        )
        .subscribe();
    },
    [bloc, orgSubscriptions, paymentMethods],
  );

  const datatables = {
    columns: [
      ...data.columns,
      {
        label: 'Action',
        field: 'action',
        minimal: 'sm',
        width: 80,
      },
    ],
    rows: [
      ...(data.rows || []).map((p, order) => {
        return {
          ...p,
          name: (
            <div className="d-flex align-items-center">
              <span className="flex-fill">{p.name}</span>
              {p.default && <Badge>Default</Badge>}
              {!p.default && (
                <a className="text-primary" onClick={() => makeDefault(p.id)}>
                  Make Default
                </a>
              )}
            </div>
          ),
          cartType: (
            <Fragment>
              <WithTooltip tooltip={p.type === PaymentMethodType.card ? 'Credit Card' : 'Bank Account'}>
                <i className={`mr-2 fas fa-${p.type === PaymentMethodType.card ? 'credit-card' : 'university'}`}></i>
              </WithTooltip>
              {p.cardType}
            </Fragment>
          ),
          nextSteps: (
            <Fragment>
              {p.nextSteps && (
                <a href={p.nextSteps?.url} target="_blank" rel="noopener noreferrer">
                  Verify
                </a>
              )}
            </Fragment>
          ),
          action: (
            <div key={`method-${order}`}>
              <div style={{ textAlign: 'center' }}>
                <a className="text-primary" onClick={() => onRemove(p.id, p.subscriptions)}>
                  {!!p.subscriptions.length ? (
                    <>
                      <i id={`btnDelete${p.id}`} className="bx bx-trash font-size-16 text-dark"></i>
                      <UncontrolledTooltip placement="left" target={`btnDelete${p.id}`}>
                        It is linked with following subscriptions.
                        <br />
                        <br />
                        {HTMLService.toReact(p.subscriptions.join('<br/>'))}
                      </UncontrolledTooltip>
                    </>
                  ) : (
                    <i className="bx bx-trash font-size-16 text-danger"></i>
                  )}
                </a>
              </div>
            </div>
          ),
        };
      }),
    ],
  };

  const history = useHistory();
  const isBillingEnabled = useMemo(() => IntegrationService.isGroupEnabled(IntegrationGroup.payment), []);
  const isAdmin = useMemo(() => user && user.role === UserRole.admin, [user]);

  if (!isBillingEnabled) {
    if (!isAdmin) return null;
    return (
      <EmptySection
        card
        icon="bx bx-credit-card"
        title="Payment Integration"
        description="You don't have any existing payment methods hooked up. Go to the integrations page to add one now."
        actionTitle="Go to integrations"
        action={() => history.push('/settings/integrations')}
      ></EmptySection>
    );
  }

  const onPaymentClick = useCallback(() => openAddEditPayment({ orgId, importType }), [importType, orgId]);

  const onPaymentSelect = useCallback(
    (type: IntegrationType) => {
      if (type === IntegrationType.stripe) {
        openStripeFormModal({ orgId });
      } else {
        openAddEditPayment({ orgId, importType });
      }
    },
    [importType, orgId],
  );

  const onAddPayment = useCallback(() => {
    if (methods?.length === 1 && firstMethod) {
      onPaymentSelect(firstMethod.type);
    } else {
      openSelectPaymentIntegrationModel({ onSelect: onPaymentSelect });
    }
  }, [firstMethod, methods, onPaymentSelect]);

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          Payment Methods
          <div className="d-flex flex-column align-items-center">
            <div>
              {/* <Button intent="primary" onClick={() => openStripeFormModal({ orgId })}> */}
              <Button intent="primary" onClick={() => onAddPayment()}>
                Add Payment Method
              </Button>
            </div>
            {/* <div>
              <Button intent="primary" onClick={onPaymentClick}>
                Add Payment Method
              </Button>
            </div> */}
            <div className="mt-1">
              <a onClick={onCopyPaymentLink} className="font-size-12 text-muted">
                Copy Payment Link
              </a>
            </div>
          </div>
        </CardTitle>
        <MDBDataTable
          responsive
          striped
          bordered
          data={datatables}
          pagesAmount={4}
          noBottomColumns
          sortable={false}
          info={false}
          paging={false}
          searching={false}
          paginationLabel={['Prev', 'Next']}
        />
      </CardBody>
    </Card>
  );
});
